import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LocalePublicToggle } from '../LocaleSelector';
import {
  LayoutDashboard,
  Users,
  Link as LinkIcon,
  FileText,
  Newspaper,
  Baby,
  UserPlus,
  TrendingUp,
  CheckSquare,
  Folder,
  BarChart3,
  MessageSquarePlus,
  Shield,
  ToggleRight,
  Sliders,
  Settings,
  Heart,
  Key,
  X,
  Inbox,
  List,
  Scale,
  ChevronDown,
  ChevronRight,
  Calendar,
  Bot,
  HelpCircle,
  Wrench,
  ListOrdered,
  Send,
  Edit,
  Package,
  FlaskConical,
  FileText as FileTextIcon,
  BookOpen,
  MessageSquare,
  Sparkles,
  Timer,
  Activity,
  AudioLines,
  Megaphone,
} from 'lucide-react';

// Types
interface NavItemConfig {
  to: string;
  icon: React.ElementType;
  label: string;
  badge?: string;
}

interface NavGroupConfig {
  id: string;
  icon: React.ElementType;
  label: string;
  children: (NavItemConfig | NavSubGroupConfig)[];
}

interface NavSubGroupConfig {
  id: string;
  label: string;
  children: NavItemConfig[];
}

// Navigation structure
const navigationConfig: (NavItemConfig | NavGroupConfig)[] = [
  // Dashboard - Direct link
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },

  // Nutzer
  {
    id: 'users',
    icon: Users,
    label: 'Nutzer',
    children: [
      { to: '/users', icon: Users, label: 'Alle Benutzer' },
      { to: '/partners', icon: LinkIcon, label: 'Partner-Verbindungen' },
      { to: '/settings/beta-codes', icon: Key, label: 'Beta- & Promo-Codes' },
      { to: '/settings/premium-content', icon: Sparkles, label: 'Premium-Flow' },
    ],
  },

  // Inhalte
  {
    id: 'content',
    icon: FileText,
    label: 'Inhalte',
    children: [
      { to: '/content/ssw', icon: FileText, label: 'SSW-Inhalte' },
      { to: '/content/blog', icon: Newspaper, label: 'Blog-Einträge' },
      { to: '/content/recommendations', icon: Megaphone, label: 'Empfehlungen' },
      // Benachrichtigungen (Subgroup)
      {
        id: 'notifications',
        label: 'Benachrichtigungen',
        children: [
          { to: '/content/notifications/broadcast', icon: Send, label: 'Broadcast senden' },
          { to: '/content/notifications/templates', icon: Edit, label: 'Texte anpassen' },
        ],
      },
    ],
  },

  // Funktionen
  {
    id: 'features',
    icon: Wrench,
    label: 'Funktionen',
    children: [
      // Namen (Subgroup)
      {
        id: 'names',
        label: 'Namen',
        children: [
          { to: '/names', icon: Baby, label: 'Alle Namen' },
          { to: '/names/suggestions', icon: UserPlus, label: 'Community-Vorschläge' },
          { to: '/names/stats', icon: TrendingUp, label: 'Statistiken' },
        ],
      },
      // Checklisten - Direct link
      { to: '/checklists', icon: CheckSquare, label: 'Checklisten' },
      // Direct items
      { to: '/features/calendar', icon: Calendar, label: 'Kalender' },
      { to: '/features/ai-chat', icon: Bot, label: 'KI-Chat' },
      { to: '/features/contraction-timer', icon: Timer, label: 'Wehenzähler' },
      { to: '/features/baby-care', icon: Baby, label: 'Baby-Care' },
      { to: '/features/sleep-sounds', icon: AudioLines, label: 'Schlafklänge' },
      { to: '/features/baby-development', icon: Activity, label: 'Baby-Entwicklung' },
      // Produktcheck (Subgroup)
      {
        id: 'product-check',
        label: 'Produktcheck',
        children: [
          { to: '/features/product-check', icon: FlaskConical, label: 'Inhaltsstoffe' },
          { to: '/features/product-check/products', icon: Package, label: 'Manuelle Produkte' },
          { to: '/features/product-check/suggestions', icon: MessageSquarePlus, label: 'Nutzer-Vorschläge' },
        ],
      },
      // Behördenguides (Subgroup)
      {
        id: 'guides',
        label: 'Behördenguides',
        children: [
          { to: '/features/guides', icon: FileTextIcon, label: 'Guides' },
          { to: '/features/guides/categories', icon: Folder, label: 'Kategorien' },
          { to: '/features/guides/terms', icon: BookOpen, label: 'Fachbegriffe' },
          { to: '/features/guides/faqs', icon: MessageSquare, label: 'Häufige Fragen' },
          { to: '/features/guides/submissions', icon: MessageSquarePlus, label: 'Nutzer-Vorschläge' },
        ],
      },
      // Verwaltung (Subgroup)
      {
        id: 'feature-management',
        label: 'Verwaltung',
        children: [
          { to: '/features/tool-menu', icon: ListOrdered, label: 'Tool-Menü' },
          { to: '/features/toggles', icon: ToggleRight, label: 'Feature-Toggles' },
          { to: '/features/limits', icon: Sliders, label: 'Limits' },
          { to: '/features/dashboard', icon: Activity, label: 'Dashboard & Activity Feed' },
        ],
      },
    ],
  },

  // Support & Hilfe
  {
    id: 'support',
    icon: HelpCircle,
    label: 'Support & Hilfe',
    children: [
      // FAQ (Subgroup)
      {
        id: 'faq',
        label: 'FAQ',
        children: [
          { to: '/faq/categories', icon: Folder, label: 'Kategorien' },
          { to: '/faq/items', icon: List, label: 'FAQ-Einträge' },
        ],
      },
      // Direct items
      { to: '/support/requests', icon: Inbox, label: 'Kontaktanfragen' },
      { to: '/support/beta-feedback', icon: MessageSquarePlus, label: 'Beta Feedback' },
      { to: '/support/gdpr', icon: Shield, label: 'DSGVO-Anfragen' },
    ],
  },

  // Analytics - Direct link
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },

  // Einstellungen
  {
    id: 'settings',
    icon: Settings,
    label: 'Einstellungen',
    children: [
      { to: '/settings/legal', icon: Scale, label: 'Rechtliches' },
      { to: '/settings/system', icon: Settings, label: 'System' },
    ],
  },
];

// Check if a path is active (for groups)
function isGroupActive(group: NavGroupConfig, pathname: string): boolean {
  return group.children.some((child) => {
    if ('to' in child) {
      return pathname === child.to || pathname.startsWith(child.to + '/');
    }
    // Subgroup
    return child.children.some(
      (item) => pathname === item.to || pathname.startsWith(item.to + '/')
    );
  });
}

// Check if subgroup is active
function isSubGroupActive(subgroup: NavSubGroupConfig, pathname: string): boolean {
  return subgroup.children.some(
    (item) => pathname === item.to || pathname.startsWith(item.to + '/')
  );
}

// Nav Item Component
interface NavItemProps {
  item: NavItemConfig;
  onClick?: () => void;
  indent?: number;
}

function NavItem({ item, onClick, indent = 0 }: NavItemProps) {
  const Icon = item.icon;
  const paddingLeft = indent === 0 ? 'pl-4' : indent === 1 ? 'pl-10' : 'pl-14';

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 ${paddingLeft} pr-4 py-2.5 text-sm transition-all ${
          isActive
            ? 'bg-teal-500/10 border-l-3 border-teal-500 text-teal-400 font-medium'
            : 'text-slate-300 hover:bg-white/5 hover:text-white'
        }`
      }
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="truncate">{item.label}</span>
      {item.badge && (
        <span className="ml-auto text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full flex-shrink-0">
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}

// Nav Subgroup Component (second level)
interface NavSubGroupProps {
  subgroup: NavSubGroupConfig;
  onClick?: () => void;
}

function NavSubGroup({ subgroup, onClick }: NavSubGroupProps) {
  const location = useLocation();
  const isActive = isSubGroupActive(subgroup, location.pathname);
  const [isOpen, setIsOpen] = useState(isActive);

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-3 pl-10 pr-4 py-2 text-sm transition-all ${
          isActive
            ? 'text-teal-400 font-medium'
            : 'text-slate-400 hover:text-slate-200'
        }`}
      >
        <span className="truncate">{subgroup.label}</span>
        {isOpen ? (
          <ChevronDown className="w-3.5 h-3.5 ml-auto flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 ml-auto flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="py-1">
          {subgroup.children.map((item) => (
            <NavItem key={item.to} item={item} onClick={onClick} indent={2} />
          ))}
        </div>
      )}
    </div>
  );
}

// Nav Group Component (first level - collapsible)
interface NavGroupProps {
  group: NavGroupConfig;
  onClick?: () => void;
}

function NavGroup({ group, onClick }: NavGroupProps) {
  const location = useLocation();
  const isActive = isGroupActive(group, location.pathname);
  const [isOpen, setIsOpen] = useState(isActive);
  const Icon = group.icon;

  return (
    <div className="mb-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm rounded-lg transition-all ${
          isActive
            ? 'bg-slate-700/50 text-white font-medium'
            : 'text-slate-300 hover:bg-white/5 hover:text-white'
        }`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className="truncate">{group.label}</span>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="mt-1 ml-2 border-l border-slate-700 py-1">
          {group.children.map((child) => {
            if ('to' in child) {
              // Direct nav item
              return <NavItem key={child.to} item={child} onClick={onClick} indent={1} />;
            }
            // Subgroup
            return <NavSubGroup key={child.id} subgroup={child} onClick={onClick} />;
          })}
        </div>
      )}
    </div>
  );
}

// Main Sidebar Component
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  // Close sidebar on navigation (mobile only)
  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop (mobile) */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-50
          w-64 bg-slate-800 text-white flex-shrink-0
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col h-screen
        `}
      >
        {/* Logo & Close Button */}
        <div className="p-4 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-teal-500 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-base font-bold">ParentPath</h1>
                <p className="text-xs text-slate-400">Admin Panel</p>
              </div>
            </div>
            {/* Close Button (Mobile) */}
            <button
              onClick={onClose}
              className="lg:hidden p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* i18n Public Toggle */}
        <div className="px-4 py-3 border-b border-slate-700 flex-shrink-0">
          <LocalePublicToggle />
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3">
          {navigationConfig.map((item) => {
            if ('to' in item) {
              // Direct nav item (Dashboard, Analytics)
              return (
                <div key={item.to} className="mb-1">
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    onClick={handleNavClick}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 text-sm rounded-lg transition-all ${
                        isActive
                          ? 'bg-teal-500/10 border-l-3 border-teal-500 text-teal-400 font-medium'
                          : 'text-slate-300 hover:bg-white/5 hover:text-white'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                </div>
              );
            }
            // Nav group
            return <NavGroup key={item.id} group={item} onClick={handleNavClick} />;
          })}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-slate-700 flex-shrink-0">
          <div className="text-xs text-slate-500 text-center">
            v1.0.0
          </div>
        </div>
      </aside>
    </>
  );
}
